'use client';

import { For, Icon, SegmentGroup, Stack, useBreakpointValue } from '@chakra-ui/react';
import { Check } from 'lucide-react';
import { forwardRef, useMemo } from 'react';
import { Radio, RadioGroup } from './radio';

interface Item {
    value: string;
    label: React.ReactNode;
    disabled?: boolean;
}

export interface SegmentedControlProps extends SegmentGroup.RootProps {
    items: Array<string | Item>;
    responsive?: boolean;
}

function normalize(items: Array<string | Item>): Item[] {
    return items.map((item) => {
        if (typeof item === 'string') {
            return { value: item, label: item };
        }
        return item;
    });
}

export const SegmentedControl = forwardRef<HTMLDivElement, SegmentedControlProps & { deselectable?: boolean }>(
    function SegmentedControl(props, ref) {
        const { deselectable, responsive, value, onClick, items, onValueChange, ...rest } = props;
        const data = useMemo(() => normalize(items), [items]);
        const selectedValue = typeof value === 'string' ? value : '';
        const isCompact = useBreakpointValue({ base: true, xl: false }, { fallback: 'base' });

        if (responsive && isCompact !== false) {
            return (
                <RadioGroup {...rest} value={selectedValue} onValueChange={onValueChange} deselectable={deselectable}>
                    <Stack gap="3" ref={ref}>
                        {data.map((item) => (
                            <Radio key={item.value} value={item.value} disabled={item.disabled}>
                                {item.label}
                            </Radio>
                        ))}
                    </Stack>
                </RadioGroup>
            );
        }

        return (
            <SegmentGroup.Root
                {...rest}
                value={value}
                onValueChange={onValueChange}
                ref={ref}
                onClick={(e) => {
                    if (deselectable && (e.target as HTMLInputElement).value === value) {
                        onValueChange?.({ value: '' });
                    }
                    onClick?.(e);
                }}
                colorPalette="purple"
                css={{
                    '& > [data-part=root]': { borderRadius: '4px' },
                }}
                w="fit-content"
                h="36px">
                <SegmentGroup.Indicator bg="purple.100" _dark={{ bg: 'purple.700' }} borderRadius="4px" />
                <For each={data}>
                    {(item) => (
                        <SegmentGroup.Item
                            key={item.value}
                            value={item.value}
                            disabled={item.disabled}
                            height="100%"
                            css={{
                                '&[data-state=checked]': {
                                    bg: 'purple.100',
                                    color: 'musa.light.100',
                                },
                                '&[data-state=checked][data-part=item]': {
                                    borderRadius: '4px',
                                },
                            }}
                            _dark={{
                                '&[data-state=checked]': {
                                    bg: 'purple.700',
                                },
                            }}>
                            <SegmentGroup.ItemText fontSize="12px">
                                {item.value === selectedValue ? (
                                    <Icon boxSize="20px" stroke="currentColor" pr="1">
                                        <Check />
                                    </Icon>
                                ) : null}
                                {item.label}
                            </SegmentGroup.ItemText>
                            <SegmentGroup.ItemHiddenInput />
                        </SegmentGroup.Item>
                    )}
                </For>
            </SegmentGroup.Root>
        );
    },
);
